import React, { useState, useEffect } from 'react';
import { 
  FaFile, 
  FaQuestionCircle, 
  FaComments,
  FaPlay,
  FaChartLine,
  FaClock,
  FaArrowRight,
  FaUsers,
  FaDownload,
  FaEye,
  FaTrendingUp,
  FaTrendingDown,
  FaSpinner,
  FaExclamationTriangle,
  FaSync
} from 'react-icons/fa';
// Removed unused chart imports
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/user`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalQuestions: 0,
    totalLessons: 0,
    totalChats: 0,
    totalUsers: 0,
    recentActivity: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch all data in parallel with correct endpoints
      const [
        documentsRes,
        questionsRes,
        lessonsRes,
        usersRes
      ] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/all-doc-data?page=1&limit=1000`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/questions?page=1&limit=1000`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/all-lessons?page=1&limit=1000`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/all-users?page=1&limit=1000`)
      ]);

      // Calculate totals with proper error handling
      const totalDocuments = documentsRes.status === 'fulfilled' ? (documentsRes.value.data?.totalItems || documentsRes.value.data?.data?.length || 0) : 0;
      const totalQuestions = questionsRes.status === 'fulfilled' ? (questionsRes.value.data?.totalItems || questionsRes.value.data?.questions?.length || 0) : 0;
      const totalLessons = lessonsRes.status === 'fulfilled' ? (lessonsRes.value.data?.totalItems || lessonsRes.value.data?.data?.length || 0) : 0;
      const totalUsers = usersRes.status === 'fulfilled' ? (usersRes.value.data?.totalItems || usersRes.value.data?.users?.length || 0) : 0;
      
      // Calculate total chats from users data
      let totalChats = 0;
      if (usersRes.status === 'fulfilled' && usersRes.value.data?.users) {
        totalChats = usersRes.value.data.users.reduce((acc, user) => {
          return acc + (user.chats ? user.chats.length : 0);
        }, 0);
      }

      // Generate recent activity from actual data with error handling
      let recentActivity = [];
      try {
        recentActivity = generateRecentActivity(
          documentsRes.status === 'fulfilled' ? (documentsRes.value.data?.data || documentsRes.value.data?.documents || []) : [],
          questionsRes.status === 'fulfilled' ? (questionsRes.value.data?.questions || questionsRes.value.data?.data || []) : [],
          lessonsRes.status === 'fulfilled' ? (lessonsRes.value.data?.data || lessonsRes.value.data?.lessons || []) : [],
          usersRes.status === 'fulfilled' ? (usersRes.value.data?.users || usersRes.value.data?.data || []) : []
        );
      } catch (error) {
        console.warn('Error generating recent activity:', error);
      }

      // Debug logging
      console.log('Dashboard API Responses:', {
        documents: documentsRes.status === 'fulfilled' ? documentsRes.value.data : 'Failed',
        questions: questionsRes.status === 'fulfilled' ? questionsRes.value.data : 'Failed',
        lessons: lessonsRes.status === 'fulfilled' ? lessonsRes.value.data : 'Failed',
        users: usersRes.status === 'fulfilled' ? usersRes.value.data : 'Failed'
      });

      console.log('Calculated Stats:', {
        totalDocuments,
        totalQuestions,
        totalLessons,
        totalChats,
        totalUsers
      });

      setStats({
        totalDocuments,
        totalQuestions,
        totalLessons,
        totalChats,
        totalUsers,
        recentActivity
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please check your connection and try again.');
      
      // Show empty state when API fails - NO static data
      setStats({
        totalDocuments: 0,
        totalQuestions: 0,
        totalLessons: 0,
        totalChats: 0,
        totalUsers: 0,
        recentActivity: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Removed unused chart generation functions

  const generateRecentActivity = (documents, questions, lessons, users) => {
    const activities = [];
    
    // Add recent documents
    documents.slice(0, 2).forEach(doc => {
      activities.push({
        type: 'document',
        message: `Document uploaded: "${doc.filename}"`,
        time: '2 hours ago',
        icon: FaFile
      });
    });
    
    // Add recent questions
    questions.slice(0, 1).forEach(question => {
      activities.push({
        type: 'question',
        message: `Question updated: "${question.question.substring(0, 50)}..."`,
        time: '4 hours ago',
        icon: FaQuestionCircle
      });
    });
    
    // Add recent lessons
    lessons.slice(0, 1).forEach(lesson => {
      activities.push({
        type: 'lesson',
        message: `Lesson published: "${lesson.lessonTitle}"`,
        time: '1 day ago',
        icon: FaPlay
      });
    });
    
    // Add recent user activity
    users.slice(0, 1).forEach(user => {
      if (user.chats && user.chats.length > 0) {
        activities.push({
          type: 'chat',
          message: `User completed questionnaire`,
          time: '6 hours ago',
          icon: FaComments
        });
      }
    });
    
    return activities.sort((a, b) => {
      const timeOrder = { '2 hours ago': 1, '4 hours ago': 2, '6 hours ago': 3, '1 day ago': 4 };
      return timeOrder[a.time] - timeOrder[b.time];
    });
  };

  const statCards = [
    {
      title: 'Documents',
      value: stats.totalDocuments,
      icon: FaFile,
      color: 'blue',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Questions',
      value: stats.totalQuestions,
      icon: FaQuestionCircle,
      color: 'green',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Lessons',
      value: stats.totalLessons,
      icon: FaPlay,
      color: 'purple',
      change: '+5%',
      changeType: 'positive'
    },
    {
      title: 'Users',
      value: stats.totalUsers,
      icon: FaUsers,
      color: 'indigo',
      change: '+15%',
      changeType: 'positive'
    },
    {
      title: 'Chat Sessions',
      value: stats.totalChats,
      icon: FaComments,
      color: 'orange',
      change: '+23%',
      changeType: 'positive'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
      green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
      purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
      orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800'
    };
    return colors[color] || colors.blue;
  };

  const getActivityIcon = (type) => {
    const icons = {
      document: FaFile,
      question: FaQuestionCircle,
      chat: FaComments,
      lesson: FaPlay
    };
    return icons[type] || FaClock;
  };

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Loading Dashboard</h3>
          <p className="text-gray-600 dark:text-gray-400">Please wait while we fetch your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Welcome back! Here's what's happening with your SmartStart AI platform.
            </p>
          </div>
          <button
            onClick={fetchDashboardData}
            disabled={isLoading}
            className="p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-all duration-200 hover:scale-105 disabled:hover:scale-100 shadow-lg hover:shadow-xl"
            title={isLoading ? 'Refreshing data...' : 'Refresh dashboard data'}
          >
            <FaSync className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <FaExclamationTriangle className="w-5 h-5" />
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card, index) => {
          const IconComponent = card.icon;
          return (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl border ${getColorClasses(card.color)}`}>
                  <IconComponent className="w-6 h-6" />
                </div>
                <div className="flex items-center text-sm font-medium text-green-600 dark:text-green-400">
                  <FaChartLine className="w-4 h-4 mr-1" />
                  {card.change}
                </div>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {card.value}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {card.title}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Quick Actions
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <a
                href="/admin/docs"
                className="group p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <FaFile className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Manage Docs
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">
                      Upload & organize
                    </p>
                  </div>
                  <FaArrowRight className="w-4 h-4 text-blue-600 dark:text-blue-400 ml-auto group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
                </div>
              </a>

              <a
                href="/admin/questions"
                className="group p-4 bg-green-50 dark:bg-green-900/20 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <FaQuestionCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                      Questions
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-300">
                      Edit questionnaire
                    </p>
                  </div>
                  <FaArrowRight className="w-4 h-4 text-green-600 dark:text-green-400 ml-auto group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
                </div>
              </a>

              <a
                href="/admin/lessons"
                className="group p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <FaPlay className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <div>
                    <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                      Lessons
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-300">
                      Manage content
                    </p>
                  </div>
                  <FaArrowRight className="w-4 h-4 text-purple-600 dark:text-purple-400 ml-auto group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
                </div>
              </a>

              <a
                href="/admin/chat-history"
                className="group p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <FaComments className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  <div>
                    <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                      Chat History
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-300">
                      View conversations
                    </p>
                  </div>
                  <FaArrowRight className="w-4 h-4 text-orange-600 dark:text-orange-400 ml-auto group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
